import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '@/widgets/header/Header';
import { Sidebar } from '@/widgets/sidebar/Sidebar';
import { EmailVerifyBanner } from '@/widgets/email-verify-banner/EmailVerifyBanner';
import { NoMatchingSetupModal } from '@/widgets/matching-setup-modal/NoMatchingSetupModal';
import { StarterWelcomeModal } from '@/widgets/starter-welcome-modal/StarterWelcomeModal';
import { useDocumentTitle } from '@/app/routes/useDocumentTitle';
import { useActivityWebSocket } from '@/shared/lib/realtime/useActivityWebSocket';
import { apiClient } from '@/shared/api/client';
import i18n, { isSupportedLocale } from '@/shared/i18n';

export function AppLayout() {
  const { t } = useTranslation();
  useDocumentTitle();
  const liveTitle = useActivityWebSocket();
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    if (typeof localStorage === 'undefined' || !localStorage.getItem('access_token')) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await apiClient.get('profile/me').json<{ locale?: string }>();
        if (cancelled || !p?.locale) return;
        if (isSupportedLocale(p.locale) && p.locale !== i18n.language) {
          await i18n.changeLanguage(p.locale);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (liveTitle) {
      setBanner(liveTitle);
      const t = window.setTimeout(() => setBanner(null), 8000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [liveTitle]);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-secondary">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        <EmailVerifyBanner />
        {banner && (
          <div
            className="border-b border-primary-200 bg-primary-50 px-6 py-2 text-sm text-primary-900"
            role="status"
          >
            {t('layout.liveEvent', { title: banner })}
          </div>
        )}
        <main className="min-w-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
      <StarterWelcomeModal />
      <NoMatchingSetupModal />
    </div>
  );
}
